import React, { FC, useEffect, useState } from "react";
import { Formik, FormikHelpers, FormikValues, Form } from "formik";
import { Button, Stack, Typography } from "@mui/material";
import * as yup from "yup";
import toast from "react-hot-toast";
import { FieldText } from "../../../../../../components/fields/Text/Text";
import axiosInstant from "../../../../../../api";
import { updateEmbed as updateEmbedApi } from "../../../../../../api/endpoints";
import { AntSwitch } from "../../../../videos/selectedVideoCardV2/AntSwitchDesign";
import { IEmbed } from "../../../embedsV2/interface";
import { ICreateEmbed } from "../constants";

const storySettingsSchema = yup.object().shape({
  name: yup.string().required(),
});

interface Props {
  embed?: IEmbed;
  height?: number;
  onComplete: (data: any) => void;
  updateEmbed: (data: Partial<ICreateEmbed>) => void;
  onCreateStory?: () => void;
}

export const GeneralSettings: FC<Props> = ({
  embed,
  height,
  onComplete,
  updateEmbed,
  onCreateStory,
}) => {
  const { name, brand_id: brandId, id, published } = embed || {};
  const [publishedValue, setPublishedValue] = useState(published);
  const [initialValues, setInitValues] = useState({ name, published });

  const handleSubmit = async (
    values: FormikValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>
  ) => {
    const payload = { name: values.name, brandId, published: publishedValue };
    if (id) {
      const updateStoryUrl = updateEmbedApi(id);
      try {
        const response = await axiosInstant.patch(updateStoryUrl, payload);
        const { status } = response;
        if (status === 200) {
          toast.success(`Story ${values.name} settings has been updated.`);
          onComplete(payload);
        } else toast.error(`Some error encountered.`);
      } catch (error) {
        toast.error(`Some error encountered.`);
      }
    } else onCreateStory?.();

    // always setSubmitting false at the end of the function
    setSubmitting(false);
  };

  const handlePublishedChange = () => {
    const updatedValue = !publishedValue;
    updateEmbed({ published: updatedValue });
    setPublishedValue(updatedValue);
  };

  useEffect(() => {
    setInitValues({ name, published });
    setPublishedValue(published);
  }, [name, published]);

  return (
    <div
      className="p-6 pb-0 relative h-full"
      style={{ height: height || 200 }}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={storySettingsSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            {!embed && props.values
              ? updateEmbed({ ...props.values, published: publishedValue })
              : null}
            <Stack width={1} spacing={4}>
              <Stack width={1} spacing={1}>
                <FieldText
                  name="name"
                  label="Story name"
                  size="small"
                  placeholder="Story Name"
                  className="border-0 rounded-xl bg-surface-2"
                  style={{ width: 350 }}
                />
              </Stack>
              <Stack direction="row" spacing={10} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <AntSwitch
                    checked={publishedValue}
                    inputProps={{ "aria-label": "ant design" }}
                    onClick={handlePublishedChange}
                  />
                  <Typography>Published</Typography>
                </Stack>
              </Stack>
            </Stack>
            <div className="absolute inset-0 top-auto border-t border-border-token pt-4 pr-6 flex items-center justify-end">
              <Button
                size="large"
                type="submit"
                variant="contained"
                disableElevation
                sx={{ textTransform: "capitalize" }}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
